/**
 * One viewer credential per report page: the token source refreshes it, and the first answer
 * also tells the page which objects it may draw and how. Rasters come from the tiler; photo
 * points, photographs and vector layers come from the bucket through signed links.
 *
 * `fetchToken` and `fetchLink` must be stable (a `useCallback`), since a new one starts afresh.
 */
import { useEffect, useMemo, useState } from 'react';
import type { ViewerLink, ViewerToken } from '@/api/viewer';
import { createLinkSource, type LinkSource } from '@/map/linkSource';
import { createTokenSource } from '@/map/tokenSource';

export interface ReportViewer {
  source: ReturnType<typeof createTokenSource>;
  linkSource: LinkSource;
  credential: ViewerToken | null;
  /** The token could not be minted: the page can draw nothing and has to say so. */
  error: unknown;
}

export function useReportViewer(
  fetchToken: () => Promise<ViewerToken>,
  fetchLink: (object: string, path: string) => Promise<ViewerLink>,
): ReportViewer {
  const [credential, setCredential] = useState<ViewerToken | null>(null);
  const [error, setError] = useState<unknown>(null);
  const source = useMemo(
    () =>
      createTokenSource(async () => {
        const credential = await fetchToken();
        setCredential(credential);
        return credential;
      }),
    [fetchToken],
  );
  useEffect(() => {
    void source.getToken().catch(setError);
  }, [source]);
  const linkSource = useMemo(() => createLinkSource(fetchLink), [fetchLink]);
  return { source, linkSource, credential, error };
}
